<template>
  <div>
    <div class="grid-9 mb-40">
      <bulletin-button
        class="span-12-md mb-20-md span-3"
        @click="toggleUploadModal"
      >
        Import from .CSV
      </bulletin-button>
      <import-products-modal
        v-if="showUploadModal"
        @close-import-products-modal="toggleUploadModal"
        :isInventory="true"
        @inventory-csv-uploaded="getPageOfProducts(1)"
      />
    </div>
    <div class="grid-9">
      <div class="grid-9 span-9 grid-column-gap-28 mb-40">
        <div class="span-3 span-12-md mb-20-md">
          <dropdown
            :placeholder="'Filter by Updated Date'"
            :dropdownItems="filterDropdownItems"
            :onSelect="filterResults"
          />
        </div>
        <div class="span-3 span-12-md mb-20-md">
          <dropdown
            :placeholder="'Filter by Stock Availability'"
            :dropdownItems="InStockDropdownItems"
            :onSelect="filterInStockResults"
          />
        </div>
        <div class="span-3 span-12-md">
          <table-search
            class="span-3 span-12-md mb-20-md"
            :search="search" :value="query"
          >
          </table-search>
        </div>
      </div>
    </div>
    <div class="grid-9 grid-column-gap-0">
      <div class="sidebar-wrapper span-2 hide-sm hide-md">
        <slot />
      </div>
      <div class="dashboard-table span-7 span-9-sm overflow-scroll">
        <table class="table-fixed">
          <thead class="bg-light-grey">
          <tr>
            <th style="width:248px">Product Name</th>
            <th style="width:111px">Track inventory</th>
            <th style="width:146px">Variant</th>
            <th style="width:146px">SKU</th>
            <th style="width:148px">INVENTORY</th>
            <th style="width:126px">
              <bulletin-button
                size="xsm"
                :disabled="products.length === 0 || !enableAddAll"
                @click="handleAddAllClick"
              >
                Save All
              </bulletin-button>
            </th>
          </tr>
          </thead>
          <tbody>
          <loading-bar @upload-complete="getPageOfDrafts(1)" />
          <template v-for="product in products">
            <inventory-record
              :key="product.product_id + timestamp"
              :data="product"
            />
          </template>
          </tbody>
        </table>
        <div v-if="products.length === 0" class="flex align-center justify-center my-50">
          <div v-if="fetchingProducts">
            <loader-static />
          </div>
          <div v-else> no products found</div>
        </div>
      </div>
    </div>
    <div
      v-if="pagination && pagination.totalCount > pagination.limit"
      class="flex justify-flex-end mt-40 mb-80 mt-20-md mb-0-md"
    >
      <pagination
        :active-page="pagination.page"
        :limit="pagination.limit"
        :on-click="getPageOfProducts"
        :total="pagination.totalCount"
        class="no-padding-right"
      />
    </div>
  </div>
</template>
<script>
import InventoryRecord from "@/views/dashboard/inventory/inventoryRecord.vue";
import LoadingBar from "@/views/dashboard/drafts/loading-bar.vue";
import DraftApi from "@/services/api/draftApi";
import { mapGetters } from "vuex";
import { paramsHelper } from "@/components/mixin/paramsHelper";
import { inventoryHelper } from "@/components/mixin/inventoryHelper";
import tableSearch from "@/views/dashboard/shared/tableSearch/tableSearch.vue";
import InventoryApi from "@/services/api/inventorytApi";
import ProductApi from "@/services/api/productApi";
import ImportProductsModal from "@/views/dashboard/products/importProductsModal/importProductsModal.vue";

const DraftApiService = new DraftApi(),
  InventoryApiService = new InventoryApi(),
  ProductApiService = new ProductApi();

export default {
  name: "InventoryPanel",
  components: {
    ImportProductsModal,
    InventoryRecord,
    LoadingBar,
    tableSearch
  },
  mixins: [paramsHelper, inventoryHelper],
  data() {
    return {
      fetchingProducts: false,
      showUploadModal: false,
      query: "",
      products: [],
      validProducts: new Set(),
      productTypes: null,
      pagination: {
        limit: 33,
        page: 1
      },
      showLoader: true,
      loaderTimeout: 5000,
      enableAddAll: true,
      selectedBrand: null,
      sortOrder: "DESC",
      filter_in_stock: null,
      filterDropdownItems: [
        {
          name: "Newest to Oldest",
          value: "DESC"
        },
        {
          name: "Oldest to Newest",
          value: "ASC"
        }],
      InStockDropdownItems: [
        {
          name: "In Stock",
          value: true
        },
        {
          name: "Out of Stock",
          value: false
        }],
      timestamp: Date.now()
    };
  },
  computed: {
    ...mapGetters({
      user: "userState"
    })
  },
  async mounted() {
    await this.getPageOfProducts(this.pagination.page);
  },
  beforeMount() {
    this.parseURLParams();
  },
  methods: {
    search: async function(query) {
      this.query = query;
      await this.getPageOfProducts(1);
    },
    handleAddAllClick: async function() {
      let trackedInventoryToAdd = this.$children
        .filter((component) => component.data !== undefined) // filter out sidebar component
        .filter((child) => child.isTracked);
      for (const product of trackedInventoryToAdd) {
        const valid = await product.$validator.validate();
        if (valid === false) {
          this.$swal("Attention", "Please fill out all required fields");
          return;
        }
      }
      trackedInventoryToAdd = trackedInventoryToAdd.map((child) => {
        return {
          product_id: child.product.product_id,
          track_inventory: child.isTracked,
          variants: child.product.variants.map(variant => {
              return {
                inventory_quantity: variant.inventory_quantity,
                sku: variant.sku,
                variant_id: variant.variant_id
              };
            }
          )
        };
      });
      let untrackedInventoryToAdd = this.$children
        .filter((component) => component.data !== undefined) // filter out sidebar component
        .filter((child) => !child.isTracked);
      for (const product of untrackedInventoryToAdd) {
        const valid = await product.$validator.validate();
        if (valid === false) {
          this.$swal("Attention", "Please fill out all required fields");
          return;
        }
      }
      untrackedInventoryToAdd = untrackedInventoryToAdd.map((child) => {
        return {
          product_id: child.product.product_id,
          track_inventory: child.isTracked,
          variants: child.product.variants.map(variant => {
              return {
                in_stock: variant.in_stock,
                sku: variant.sku,
                variant_id: variant.variant_id
              };
            }
          )
        };
      });
      this.updateAllProducts(
        trackedInventoryToAdd,
        untrackedInventoryToAdd
      );
      this.$swal("Success!", "Product Inventory Updated");
    },
    filterResults: async function(filter) {
      this.sortOrder = filter.value;
      await this.getPageOfProducts(1);
    },
    filterInStockResults: async function(filter) {
      this.filter_in_stock = filter.value;
      await this.getPageOfProducts(1);
    },
    getPageOfProducts: async function(page) {
      this.fetchingProducts = true;
      this.products = [];
      const offset = page - 1,
        params = {
          order: this.sortOrder,
          page: offset,
          size: this.pagination.limit,
          ...(this.query.length > 1) && {
            keyword: this.query
          },
          ...this.filter_in_stock !== null && {
            in_stock: this.filter_in_stock
          }
        },
        { data } = await InventoryApiService.getProducts(params);
      this.timestamp = Date.now();

      this.products = data.products;
      this.fetchingProducts = false;
      this.$forceUpdate();
      this.pagination.page = page;
      this.pagination.totalCount = data.total;
      this.updateURLParams("page", this.pagination.page);
      return Promise.resolve();
    },
    parseURLParams: function() {
      const { page, brandId, brandName } = this.$route.query;
      if (brandId && brandName) {
        this.selectedBrand = { id: brandId, name: brandName };
      }
      if (Number(page)) {
        this.pagination.page = Number(page);
      }
    },
    toggleUploadModal() {
      this.showUploadModal = !this.showUploadModal;
    }
  }
};
</script>
